/**
 * Onboarding: invite → Google sign-in → lessons → 7 acknowledgements → state food handler course →
 * permit photo uploaded → manager approves. Clearance is derived, never typed by hand.
 */
import { ACKNOWLEDGEMENTS, getAck } from "./acks.ts";
import { HOUSE_ACK_ID } from "./houserules.ts";
import { LESSONS } from "./learn.ts";
import { clockStart } from "./journey.ts";
import { cardClock, shapesAt, type CardClock } from "./ruleshapes.ts";
import { HEALTH_DECISION_BLOCK_TITLE } from "./returntowork.ts";
import type { AcknowledgementSignature, Clearance, Credential, Employee, IsoDate, LessonProgress, OnboardingStep } from "./model.ts";
import { TEMP_CERT_REMIND_DAYS, type CourseOnFile } from "./certscan.ts";

import { ONBOARDING_UT } from "./states/ut/onboarding.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof ONBOARDING_UT>> = perStateTable("ONBOARDING", ONBOARDING_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

export interface OnboardingInput {
  /** The state whose rules apply (absent = Utah, the default). */
  state?: UsState;
  /**
   * The county of the person's place (with `state`): picks the health authority's food handler rule (ruleshapes.ts
   * shapesAt). Absent = the state layer only. Clark County (SNHD): the card before the first shift; Utah: 14/30 days.
   */
  county?: string | null;
  /** `endsOn` set: a one-event volunteer (their invite's last day), who gets the short gate below. */
  employee: Pick<Employee, "userId" | "startedOn"> & Partial<Pick<Employee, "endsOn">>;
  /**
   * The person who created the business at setup (Organization.createdBy is their user id). They chose the rules, so
   * the company's lessons-before-food-work policy doesn't hold them back; Utah's 30-day permit rule still does.
   */
  founder?: boolean;
  lessons: Pick<LessonProgress, "lessonId">[];
  signatures: Pick<AcknowledgementSignature, "ackId" | "ackVersion">[];
  credentials: Pick<Credential, "kind" | "expiresOn" | "verifiedAt">[];
  managerApproved: boolean;
  today: IsoDate;
  /**
   * When food work starts at this person's place (journey.ts foodWorkStart): the 14/30-day clock runs from the later of
   * that and their own start. null = the place isn't serving food yet, so the clock hasn't started. Absent = startedOn.
   */
  workStartsOn?: IsoDate | null;
}

export interface OnboardingStatus {
  steps: { step: OnboardingStep; done: boolean; label: string }[];
  lessonsDone: boolean;
  acksDone: boolean;
  nextStep: OnboardingStep | null;
  clearance: Clearance;
  missing: string[];
  /** Days left in the 14-day course / 30-day permit windows (Utah Code 26B-7-413). */
  courseDueInDays: number;
  permitDueInDays: number;
  permitExpiresInDays: number | null;
  /** False while the place isn't serving food (planning mode): the course and permit windows haven't started. */
  clockRunning: boolean;
  /** Which lessons-before-food-work rule applies: everyone's, a one-event volunteer's short one, or none (the founder). */
  track: FoodWorkTrack;
  /** The company's lessons-and-signatures rule is met for this track (shiftGate's "not_cleared" lessons block). */
  foodWorkReady: boolean;
  /** What is still needed before food work under that rule. */
  foodWorkNeeds: { lessonIds: string[]; ackIds: string[] };
  /** The food handler card on file, if any. A fact for the manager to see, never a judgment. */
  card: CardOnFile | null;
  /**
   * The place's food handler card rule (ruleshapes.ts cardClock): required here or not, each step's due day, whether a late
   * card stops food work (Utah's day 30) or only flags the hire (SNHD's before-first-shift card; never blocks safety work).
   */
  cardRule: CardClock;
  /**
   * A food handler COURSE certificate on file (fix/cert-scan), when no valid permit is: the course step is done, the permit is
   * not. `permitBy` is the earlier of the certificate's own end and the place's permit window (Utah Code 26B-7-413: 30 days).
   */
  course: CourseOnFile | null;
}

export interface CardOnFile { kind: "food_handler_permit" | "certified_food_safety_manager"; expiresOn: string | null; verified: boolean; daysLeft: number | null }

/** What a manager reads about a person's card: what is on file and when it expires. No verdict. */
export function cardLine(c: CardOnFile | null): string {
  // "Card" also means the payment card (owner 2026-10-09: "what card anyways"): the food handler credential is the permit.
  if (!c) return PERMIT_NUDGE.manager;
  const what = c.kind === "certified_food_safety_manager" ? "Manager certificate" : "Permit";
  return c.expiresOn ? `${what} on file, expires ${c.expiresOn}${c.verified ? ". Checked by a manager." : "."}` : `${what} on file${c.verified ? ", checked by a manager" : ""}.`;
}

/**
 * The gentle permit line (fix/permit-step-read, owner 2026-10-09: "we need a not now option"). A worker who tapped "Not now", or
 * whose course certificate stands in for now, sees ONE line with one tap on their own page and on Today until a permit is on
 * file; the manager sees "Permit not added yet" on that person. No alarm words, and it never blocks anything: clock-in, the
 * check-in, a sick report and every safety record go on as before (the state's own day-30 rule, shiftGate, is unchanged).
 */
export const PERMIT_NUDGE = {
  add: "Add your food handler permit",
  whenArrives: "Add your food handler permit when it arrives",
  action: "Add your permit",
  manager: "Permit not added yet.",
} as const;

export interface PermitNudge {
  /** The one line ("Add your food handler permit"). */
  line: string;
  /** Plain detail under it: the due day from the place's rule, or the course certificate's last day. Empty when neither is known. */
  detail: string;
  /**
   * A reminder moment, from dates only (no push here): days 2-3 after starting (once, after "Not now"), and the last 3 days before
   * the place's own deadline (the state pack's permit window), or the last 7 days of a course certificate (TEMP_CERT_REMIND_DAYS). Today shows the line
   * as a reminder then; otherwise it is the quiet line.
   */
  remind: boolean;
}

const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDay = (d: IsoDate) => `${MONTH[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;

/** The permit line for one person, or null when a permit is on file or the place has no permit rule. */
export function permitNudge(st: Pick<OnboardingStatus, "card" | "course" | "cardRule" | "clockRunning" | "permitDueInDays">, o: { today: IsoDate; startedOn?: IsoDate }): PermitNudge | null {
  if (!st.cardRule.required || st.card) return null;
  if (st.course) {
    const left = st.course.daysLeft;
    const detail = st.course.permitBy ? (left !== null && left < 0 ? `Your course certificate counted until ${shortDay(st.course.permitBy)}.` : `Your course certificate is good until ${shortDay(st.course.permitBy)}.`) : "";
    // A temporary certificate: the reminder starts a week before its last day (the manager's finding uses the same day).
    return { line: PERMIT_NUDGE.whenArrives, detail, remind: left !== null && left <= TEMP_CERT_REMIND_DAYS };
  }
  const since = o.startedOn ? days(o.startedOn, o.today) : 0;
  const card = st.cardRule.steps.find((x) => x.id === "card");
  const dated = st.clockRunning && card?.due.kind === "within_days";
  const due = dated ? addDaysIso(o.today, st.permitDueInDays) : null;
  const detail = due ? `${st.permitDueInDays < 0 ? "It was due" : "Due"} ${shortDay(due)}${st.cardRule.citation ? ` (${st.cardRule.citation})` : ""}.` : "";
  return { line: PERMIT_NUDGE.add, detail, remind: (since >= 2 && since <= 3) || (dated && st.permitDueInDays <= 3) };
}

/**
 * "trained": a card holder. They have a Utah food handler card on file (their photo and number, not yet checked by a
 * manager), so they skip the 9 generic lessons and 7 sign-offs and read only the house rules (houserules.ts), signing
 * once. The card itself is still due within 30 days of hire (CARD_LAW) and stays tracked until it expires.
 */
export type FoodWorkTrack = "full" | "event" | "founder" | "trained";

/**
 * One-event volunteers (docs/ux/LAUNCH-PROPOSALS.md Wave 1 #7): before food work, only the check-in (shiftGate), the
 * three critical lessons and one signature. The lessons cover the Food Code's three worker controls for the illnesses
 * food spreads most: no work while sick (2-201.11), handwashing (2-301.12) and no bare-hand contact with ready-to-eat
 * food (3-301.11). The signature is the illness-reporting promise (2-201.11), the one a worker must be told about.
 */
export const VOLUNTEER_LESSON_IDS: readonly string[] = ["sick", "hands", "barehand"];
export const VOLUNTEER_ACK_ID = "illness";
const VOLUNTEER_MINUTES = LESSONS.filter((l) => VOLUNTEER_LESSON_IDS.includes(l.id)).reduce((n, l) => n + l.minutes, 0) + 1;

/** shiftGate's lessons block: the manager's words (findings) and the worker's own (check-in result, Do, the phone). */
export const LESSONS_GATE: Record<Exclude<FoodWorkTrack, "founder">, { manager: string; worker: string }> = {
  trained: { manager: "Permit on file. Reads the house rules (4 cards) and signs once, about 1 minute on their phone.", worker: "Read the 4 house rules and tap I understand: about 1 minute on your phone." },
  full: { manager: "About 20 minutes on their phone before their first food shift.", worker: "Finish your lessons today: about 20 minutes on your phone." },
  event: { manager: `3 short lessons and one signature, about ${VOLUNTEER_MINUTES} minutes on their phone, before food work.`, worker: `Finish 3 short lessons and sign once: about ${VOLUNTEER_MINUTES} minutes on your phone.` },
};
export const lessonsGateDetail = (track: FoodWorkTrack) => LESSONS_GATE[track === "event" || track === "trained" ? track : "full"].manager;

/**
 * The same lessons block on the shop tablet (persona audit 2026-09-29 #2): a worker with no phone does the lessons right
 * there, so the tablet never says "on your phone". The lessons-before-food-work rule is the company's own (above), not
 * Utah law; Utah's food handler permit within 30 days (Utah Code 26B-7-413) is unchanged by where the lessons are done.
 */
export const TABLET_LESSONS_LINE: Record<Exclude<FoodWorkTrack, "founder">, string> = {
  trained: "Read the 4 house rules on this tablet: about 1 minute.",
  full: "Do your lessons on this tablet: about 20 minutes.",
  event: `Do 3 short lessons and sign once on this tablet: about ${VOLUNTEER_MINUTES} minutes.`,
};

/**
 * A shiftGate reason in the worker's own words ("your phone", never "their phone"). The check-in reasons have their
 * own screens; this covers the lessons and the permit.
 */
export function workerGateLine(b: { kind: string; title: string; detail: string }, o: { device?: "phone" | "tablet"; state?: UsState } = {}): string {
  if (b.kind === "not_cleared") {
    const t = (Object.values(LESSONS_GATE) as { manager: string; worker: string }[]).find((x) => x.manager === b.detail);
    if (t) return o.device === "tablet" ? TABLET_LESSONS_LINE[t === LESSONS_GATE.event ? "event" : t === LESSONS_GATE.trained ? "trained" : "full"] : t.worker;
    if (b.title.includes("30-day")) return words(o.state).permitCardLine ?? "Add your food handler permit.";
  }
  // The person in charge's decision (returntowork.ts HEALTH_DECISION_BLOCK_TITLE): a new check-in doesn't lift it.
  if (b.kind === "not_checked_in" && b.title === HEALTH_DECISION_BLOCK_TITLE) return `The person in charge decided: ${b.detail}`;
  if (b.kind === "not_checked_in") return "Do today's check-in first. About 30 seconds.";
  return b.detail;
}

const days = (from: IsoDate, to: IsoDate) => Math.round((Date.parse(to + "T00:00:00Z") - Date.parse(from + "T00:00:00Z")) / 86400000);

/**
 * The course step's name: the state's ("Utah food handler course", "California food handler card course"), unless the place's
 * own health authority issues its own card (rule shapes: an authority-layer card, e.g. Riverside County's Food Handler
 * Certificate, due within 7 days; HSC 113948(f) leaves the pre-2009 county programs in place). Then it's that card's name.
 * A state with no course words (Nevada) keeps the generic line, so Clark County's is unchanged.
 */
function courseLabel(i: OnboardingInput, rule: CardClock): string {
  const own = words(i.state).courseName;
  const r = shapesAt({ state: i.state ?? "UT", county: i.county });
  const card = rule.steps.find((x) => x.id === "card");
  return own && card && r.from.foodHandler === "authority" ? card.label : own ?? "Food handler course";
}

export function onboardingStatus(i: OnboardingInput): OnboardingStatus {
  const lessonsDone = LESSONS.every((l) => i.lessons.some((p) => p.lessonId === l.id));
  const acksDone = ACKNOWLEDGEMENTS.every((a) => i.signatures.some((s) => s.ackId === a.id && s.ackVersion >= a.version));
  const permit = i.credentials
    .filter((c) => c.kind === "food_handler_permit" || c.kind === "certified_food_safety_manager")
    .sort((a, b) => (b.expiresOn ?? "").localeCompare(a.expiresOn ?? ""))[0];
  const permitValid = !!permit && (!permit.expiresOn || permit.expiresOn >= i.today);
  const permitVerified = permitValid && !!permit?.verifiedAt;
  // A course certificate (fix/cert-scan) marks the course done, never the permit: it is not in `permit` above, so it can't
  // make permitValid true, start the "trained" track or count toward clearance. Done while it hasn't ended.
  const courseCert = i.credentials
    .filter((c) => c.kind === "food_handler_course")
    .sort((a, b) => (b.expiresOn ?? "9999").localeCompare(a.expiresOn ?? "9999"))[0];
  const courseValid = !!courseCert && (!courseCert.expiresOn || courseCert.expiresOn >= i.today);

  // The place's card rule (rule shapes): Utah's 14/30-day clock, SNHD's card before the first shift, or no card rule at all.
  const from = clockStart(i.employee.startedOn, i.workStartsOn);
  const rule = cardClock(shapesAt({ state: i.state ?? "UT", county: i.county }).shapes.foodHandler, { from, today: i.today, hasCard: permitValid });
  const steps: OnboardingStatus["steps"] = [
    { step: "invited", done: true, label: "Invited" },
    { step: "signed_in", done: !!i.employee.userId, label: "Signed in to the app" },
    { step: "lessons", done: lessonsDone, label: `Safe food prep lessons (${LESSONS.length})` },
    { step: "acknowledgements", done: acksDone, label: `Signed acknowledgements (${ACKNOWLEDGEMENTS.length})` },
    // No card rule here (Nevada outside Clark County): these two steps aren't asked.
    ...(rule.required ? [
      { step: "food_handler_course" as const, done: permitValid || courseValid, label: courseLabel(i, rule) },
      { step: "credential_uploaded" as const, done: permitVerified, label: "Permit uploaded and checked" },
    ] : []),
    { step: "manager_approved", done: i.managerApproved, label: "Manager approved" },
  ];
  const missing = steps.filter((s) => !s.done).map((s) => s.label);
  // Utah Code 26B-7-413 counts from "starting food work": not before the place serves food (journey.ts J3).
  const worked = from ? days(from, i.today) : 0;
  // A card on file (not expired) puts a hire on the short track, unless they already did every lesson and form (nothing left to skip). Precedence: founder, one-event volunteer, card holder.
  const track: FoodWorkTrack = i.founder ? "founder" : i.employee.endsOn ? "event" : permitValid && !(lessonsDone && acksDone) ? "trained" : "full";
  const needLessons = track === "founder" || track === "trained" ? [] : LESSONS.filter((l) => (track === "full" || VOLUNTEER_LESSON_IDS.includes(l.id)) && !i.lessons.some((p) => p.lessonId === l.id)).map((l) => l.id);
  const signed = (id: string) => { const a = getAck(id)!; return i.signatures.some((s) => s.ackId === a.id && s.ackVersion >= a.version); };
  const ackPool = track === "trained" ? [getAck(HOUSE_ACK_ID)!] : ACKNOWLEDGEMENTS;
  const needAcks = track === "founder" ? [] : ackPool.filter((a) => (track === "full" || track === "trained" || a.id === VOLUNTEER_ACK_ID) && !signed(a.id)).map((a) => a.id);
  // A card holder's record is the house-rules signature plus a card a manager has checked (the generic 7 forms and 9
  // lessons are not asked of them), so their clearance follows that; everyone else's is unchanged.
  const trainedDone = track === "trained" && needAcks.length === 0;
  // Where no card is required, a verified card isn't part of clearance.
  const cardOk = permitVerified || !rule.required;
  const clearance: Clearance = (acksDone || trainedDone) && i.managerApproved && cardOk ? "public_food_work" : (acksDone && lessonsDone) && i.managerApproved ? "members_only_events" : "not_cleared";
  return {
    steps,
    lessonsDone,
    acksDone,
    nextStep: steps.find((s) => !s.done)?.step ?? null,
    clearance,
    missing,
    // Days left per step of the place's rule (Utah: 14 and 30; SNHD: 0 = before the first shift). No rule: never due.
    courseDueInDays: rule.steps.find((x) => x.id === "course")?.dueInDays ?? rule.steps.find((x) => x.id === "card")?.dueInDays ?? 14 - worked,
    permitDueInDays: rule.steps.find((x) => x.id === "card")?.dueInDays ?? 30 - worked,
    permitExpiresInDays: permit?.expiresOn ? days(i.today, permit.expiresOn) : null,
    clockRunning: from !== null,
    track,
    foodWorkReady: needLessons.length === 0 && needAcks.length === 0,
    foodWorkNeeds: { lessonIds: needLessons, ackIds: needAcks },
    card: permit ? { kind: permit.kind as CardOnFile["kind"], expiresOn: permit.expiresOn ?? null, verified: !!permit.verifiedAt, daysLeft: permit.expiresOn ? days(i.today, permit.expiresOn) : null } : null,
    cardRule: rule,
    course: !permitValid && courseCert ? courseOnFile(courseCert, rule, i.today, from !== null) : null,
  };
}

/** The course certificate's permit window: the earlier of its own end and the place's permit due day (when the clock runs). */
function courseOnFile(c: Pick<Credential, "expiresOn" | "verifiedAt">, rule: CardClock, today: IsoDate, clockRunning: boolean): CourseOnFile {
  const card = rule.steps.find((x) => x.id === "card");
  const windowEnd = clockRunning && card?.dueInDays !== null && card?.dueInDays !== undefined ? addDaysIso(today, card.dueInDays) : null;
  const permitBy = [c.expiresOn ?? null, windowEnd].filter((x): x is string => !!x).sort()[0] ?? null;
  return { expiresOn: c.expiresOn ?? null, permitBy, daysLeft: permitBy ? days(today, permitBy) : null, verified: !!c.verifiedAt, citation: rule.citation };
}
const addDaysIso = (d: IsoDate, n: number) => new Date(Date.parse(d + "T00:00:00Z") + n * 86400000).toISOString().slice(0, 10);

/** The one next card of the "Start" player: what a person does now, one thing at a time (S15). */
export interface StartCard { kind: "house" | "lesson" | "ack" | "course" | "permit" | "done"; id?: string; title: string; sub?: string }

/**
 * What the Start card's button says and does (review A12.2: the web button read "Start the state course" while it
 * opened the next in-app lesson). The label names what the tap does; the state course is an outside link, never a lesson.
 */
export function startCardAction(kind: StartCard["kind"]): { label: string; external: boolean } {
  switch (kind) {
    case "house": return { label: "Read the house rules", external: false };
    case "lesson": return { label: "Start the next lesson", external: false };
    case "ack": return { label: "Read and sign", external: false };
    case "course": return { label: "Find an approved state course", external: true };
    case "permit": return { label: "Add my permit", external: false };
    case "done": return { label: "Do the check-in", external: false };
  }
}

/**
 * The single next thing, in the order that unblocks food work first (`foodWorkNeeds`), then the state course and the
 * card. `done` holds what this device already finished but the server status may not show yet.
 */
export function nextStartCard(st: Pick<OnboardingStatus, "track" | "foodWorkNeeds" | "steps" | "courseDueInDays" | "permitDueInDays"> & Partial<Pick<OnboardingStatus, "clockRunning" | "cardRule">>, done: { lessons: readonly string[]; acks: readonly string[] } = { lessons: [], acks: [] }, state?: UsState): StartCard {
  if (st.track === "trained" && st.foodWorkNeeds.ackIds.includes(HOUSE_ACK_ID) && !done.acks.includes(HOUSE_ACK_ID)) return { kind: "house", id: HOUSE_ACK_ID, title: "House rules", sub: "4 short cards, about 1 minute" };
  const lesson = LESSONS.find((l) => st.foodWorkNeeds.lessonIds.includes(l.id) && !done.lessons.includes(l.id));
  if (lesson) return { kind: "lesson", id: lesson.id, title: lesson.title, sub: `${lesson.minutes} min` };
  const ack = ACKNOWLEDGEMENTS.find((a) => st.foodWorkNeeds.ackIds.includes(a.id) && !done.acks.includes(a.id));
  if (ack) return { kind: "ack", id: ack.id, title: `Sign: ${ack.title}` };
  // Absent steps (no card rule at this place) count as done.
  const step = (k: OnboardingStep) => st.steps.find((x) => x.step === k)?.done ?? true;
  // SNHD: the card is due before the first shift (2-601(A)), one step, no day count.
  if (st.cardRule?.beforeFirstWork && !step("food_handler_course") && st.track !== "trained") return { kind: "course", title: words(state).courseName ?? "Food handler card", sub: "Needed before your first shift" };
  if (st.cardRule?.beforeFirstWork && !step("credential_uploaded") && st.track !== "trained") return { kind: "permit", title: "Your food handler permit", sub: "Needed before your first shift" };
  // The windows are the place's own (card shape): Utah's course 14 and permit 30, California's card 30, Riverside's certificate 7.
  // They were Utah's 14 and 30 everywhere, so a Los Angeles hire read "Past the 14-day window" on day 15 of a 30-day card.
  const window = (id: "course" | "card", utah: number) => { const r = st.cardRule?.steps; const x = r?.find((y) => y.id === id) ?? r?.find((y) => y.id === "card"); return x?.due.kind === "within_days" ? x.due.days : utah; };
  const courseDays = window("course", 14), cardDays = window("card", 30);
  if (!step("food_handler_course") && st.track !== "trained") return { kind: "course", title: st.steps.find((x) => x.step === "food_handler_course")?.label ?? words(state).courseName ?? "Food handler course", sub: st.clockRunning === false ? `Due within ${courseDays} days of your first day serving food` : st.courseDueInDays >= 0 ? `Finish within ${st.courseDueInDays} days` : `Past the ${courseDays}-day window` };
  if (!step("credential_uploaded") && st.track !== "trained") return { kind: "permit", title: "Your food handler permit", sub: st.clockRunning === false ? `Due within ${cardDays} days of your first day serving food` : st.permitDueInDays >= 0 ? `Due within ${st.permitDueInDays} days` : `Past the ${cardDays}-day window` };
  return { kind: "done", title: "You're set for food work", sub: "Do today's check-in, then log your first temperature." };
}

/**
 * "Send them a reminder" from a person's page (fix/permit-temp-display, owner 2026-10-10): what is left on their steps, in plain
 * words, with no health words ever (push.ts HEALTH_WORDS is checked by the server too). Once a day per person (the server says
 * "Already reminded today"). null when nothing is left to do.
 */
export const ONBOARDING_REMIND_KIND = "onboarding";
export const ONBOARDING_REMIND_ONCE = "Already reminded today. You can send another tomorrow.";
export function onboardingReminder(st: Pick<OnboardingStatus, "foodWorkNeeds" | "card" | "course" | "cardRule">, firstName: string): string | null {
  const parts: string[] = [];
  const l = st.foodWorkNeeds.lessonIds.length, a = st.foodWorkNeeds.ackIds.length;
  if (l) parts.push(`${l} lesson${l === 1 ? "" : "s"}`);
  if (a) parts.push(`${a} page${a === 1 ? "" : "s"} to sign`);
  const permit = st.cardRule.required && !st.card;
  if (!parts.length && !permit) return null;
  const todo = parts.length ? `please finish ${parts.join(" and ")} in CleanCleaver` : "";
  const add = permit ? (st.course ? "add your food handler permit when it arrives" : "add your food handler permit in CleanCleaver") : "";
  return `Hi ${firstName}, ${[todo, add].filter(Boolean).join(", and ")}.`;
}
